import { useEffect, useState } from "react";
import { Check, Lock, Search, Users, X } from "lucide-react";
import { searchUsers } from "../api";
import type { User } from "../types";
import { UserAvatar } from "./UserAvatar";

export type CreateChatInput = Readonly<{
  description?: string;
  kind: "direct" | "group";
  name?: string;
  users: User[];
  visibility: "private" | "public";
}>;

type Props = Readonly<{
  busy: boolean;
  onClose: () => void;
  onCreate: (input: CreateChatInput) => Promise<void>;
}>;

export function CreateChatModal({ busy, onClose, onCreate }: Props) {
  const [kind, setKind] = useState<"direct" | "group">("direct");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<User[]>([]);
  const [selected, setSelected] = useState<User[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void searchUsers(query).then(setResults).catch((caught) => setError(caught instanceof Error ? caught.message : "Ошибка поиска"));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  const toggle = (user: User) => {
    if (kind === "direct") {
      setSelected([user]);
      return;
    }
    setSelected((current) => current.some((item) => item.id === user.id) ? current.filter((item) => item.id !== user.id) : [...current, user]);
  };

  const submit = async () => {
    setError(null);
    if (kind === "direct" && selected.length !== 1) return setError("Выберите собеседника");
    if (kind === "group" && !name.trim()) return setError("Введите название группы");
    try {
      const input: CreateChatInput = {
        kind,
        visibility: kind === "group" && isPublic ? "public" : "private",
        users: selected,
        ...(kind === "group" ? { name: name.trim() } : {}),
        ...(kind === "group" && description.trim() ? { description: description.trim() } : {}),
      };
      await onCreate(input);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось создать чат");
    }
  };

  return (
    <div className="modal-backdrop" role="presentation">
      <section aria-labelledby="create-chat-title" aria-modal="true" className="modal-card" role="dialog">
        <header><div><p className="eyebrow">НОВЫЙ ДИАЛОГ</p><h2 id="create-chat-title">Создать защищённый чат</h2></div><button className="icon-button" onClick={onClose} type="button"><X /></button></header>
        <div className="segmented">
          <button className={kind === "direct" ? "active" : ""} onClick={() => { setKind("direct"); setSelected([]); }} type="button">Личный</button>
          <button className={kind === "group" ? "active" : ""} onClick={() => { setKind("group"); setSelected([]); }} type="button">Группа</button>
        </div>
        {kind === "group" && (
          <div className="group-fields">
            <label><span>Название</span><input maxLength={120} onChange={(event) => setName(event.target.value)} placeholder="Например, Команда проекта" value={name} /></label>
            <label><span>Описание</span><input maxLength={500} onChange={(event) => setDescription(event.target.value)} placeholder="О чём эта группа" value={description} /></label>
            <label className="switch-line"><span><Users />Открытая группа<small>Пользователи смогут отправить запрос на вступление</small></span><input checked={isPublic} onChange={(event) => setIsPublic(event.target.checked)} type="checkbox" /></label>
          </div>
        )}
        <div className="search-box modal-search"><Search size={18} /><input autoFocus onChange={(event) => setQuery(event.target.value)} placeholder="Имя или @username" value={query} /></div>
        {selected.length > 0 && <div className="selected-users">{selected.map((user) => <button key={user.id} onClick={() => toggle(user)} type="button">{user.displayName}<X size={13} /></button>)}</div>}
        <div className="user-results">
          {results.map((user) => {
            const active = selected.some((item) => item.id === user.id);
            return <button className={active ? "active" : ""} key={user.id} onClick={() => toggle(user)} type="button"><UserAvatar className="avatar small" user={user} /><span><b>{user.displayName}</b><small>@{user.username}</small></span>{active && <Check />}</button>;
          })}
          {query.length >= 2 && results.length === 0 && <p className="muted">Ничего не найдено</p>}
        </div>
        {error && <div className="form-error" role="alert">{error}</div>}
        <footer><span><Lock size={15} /> Ключ комнаты будет зашифрован отдельно для каждого устройства.</span><button className="primary-button compact" disabled={busy} onClick={() => void submit()} type="button">{busy ? "Создаём…" : "Создать чат"}</button></footer>
      </section>
    </div>
  );
}
